import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  LinearProgress,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AccountBalanceWallet as TokenIcon,
  TrendingUp,
  Diamond,
  EmojiEvents,
  Landscape,
  Construction, 
  Grass
} from "@mui/icons-material";
import { AppState } from "../store";
import { GetGameStatus, GetAssets } from "../store/user/actions";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const userModule = useSelector((state: AppState) => state.userModule);
  const { loginInfo, gameStatus, assetData } = userModule;

  useEffect(() => {
    dispatch(GetGameStatus());
    dispatch(GetAssets());
  }, [dispatch]);

  // Calculate statistics based on user data
  const totalTools = loginInfo.assets?.filter((asset: any) => asset.tool_config)?.length || 0;
  const totalDeer = loginInfo.assets?.filter((asset: any) => !asset.tool_config)?.length || 0;
  const stakedTools = loginInfo.assets?.filter((asset: any) => asset.tool_config && asset.staked)?.length || 0;
  const openWorlds = loginInfo.open_worlds?.length || 0;
  const totalWorlds = gameStatus.reward_info?.length || 0;
  
  // Get staking pools info
  const stakingPools = gameStatus.pool_info || [];
  
  // Recent transactions - this would ideally come from backend
  const recentTransactions = [
    { type: 'stake', amount: '3 Tools', time: '2 hours ago' },
    { type: 'claim', amount: '125 ANTLER', time: '1 day ago' },
    { type: 'exploration', amount: 'World #2', time: '3 days ago' },
  ];

  // Upcoming tournament data
  const upcomingTournaments = gameStatus.tournaments?.filter((tournament: any) => 
    !tournament.end_time || tournament.end_time > Date.now() / 1000
  ) || [];

  // Check for loading state
  const isLoading = gameStatus.loading || assetData?.loading;

  // Handle loading state
  if (isLoading) {
    return (
      <Box sx={{ p: 2, color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Paper sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Loading your assets and game data...
          </Typography>
          <LinearProgress color="primary" />
        </Paper>
      </Box>
    );
  }

  // Handle error state
  if (gameStatus.error || assetData?.error) {
    return (
      <Box sx={{ p: 2, color: 'white' }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Paper sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2 }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            Error loading data: {gameStatus.error || assetData?.error}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => {
            dispatch(GetGameStatus());
            dispatch(GetAssets());
          }}>
            Retry
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mt:4,p: 2, color: 'white' , background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%)'}}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {loginInfo.isLogin ? (
        <>
          {/* Account Overview */}
          <Paper 
            sx={{ 
              p: 3, 
              mb: 3, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(32,32,64,0.9) 0%, rgba(1, 1, 165, 0.8) 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          >
            <Grid container spacing={2} alignItems="center"sx={{ color: 'white' }}>
              <Grid item xs={12} md={8}>
                <Typography variant="h5" sx={{ color: 'white' }}>
                  Welcome back, {loginInfo.account}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
                  Account Overview
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 2,
                    mb: 2,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  {[
                    {
                      src: '/images/token_wax.png',
                      alt: 'WAX',
                      label: 'WAX',
                      value: parseFloat(loginInfo.balance?.wax_balance || '0').toFixed(2),
                    },
                    {
                      src: '/images/token_antlers.png',
                      alt: 'ANTLER',
                      label: 'ANTLER',
                      value: parseFloat(loginInfo.balance?.game_antlers || '0').toFixed(2),
                    },
                    {
                      src: '/images/token_gold.png',
                      alt: 'GOLD',
                      label: 'GOLD',
                      value: parseFloat(loginInfo.balance?.game_gold || '0').toFixed(2),
                    },
                    {
                      src: '/images/token_grass.png',
                      alt: 'GRASS',
                      label: 'GRASS',
                      value: parseFloat(loginInfo.balance?.game_grass || '0').toFixed(2),
                    },
                    {
                      src: '/images/xp.webp',
                      alt: 'XP',
                      label: 'XP',
                      value: parseFloat(loginInfo.balance?.game_xp || '0').toFixed(2),
                    },
                  ].map((token) => (
                    <Paper
                      key={token.label}
                      sx={{
                        p: { xs: 1, sm: 2 },
                        bgcolor: 'rgba(0,0,0,0.3)',
                        borderRadius: 2,
                        minWidth: { xs: 90, sm: 120 },
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        mb: { xs: 1, sm: 0 },
                      }}
                    >
                      <img
                        src={token.src}
                        alt={token.alt}
                        style={{
                          width: 28,
                          height: 28,
                          marginBottom: 4,
                          objectFit: 'contain',
                        }}
                      />
                      <Typography variant="caption" color="white" sx={{ fontSize: { xs: 11, sm: 13 } }}>
                        {token.label}
                      </Typography>
                      <Typography
                        variant="subtitle1"
                        color="white"
                        sx={{ fontSize: { xs: 14, sm: 18 }, fontWeight: 500 }}
                      >
                        {token.value}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <Stack direction="row" spacing={1}>
                  <Button 
                    variant="contained" 
                    color="primary"
                    onClick={() => navigate('/tools')}
                  >
                    Manage Tools
                  </Button>
                  <Button 
                    variant="outlined" 
                    sx={{ color: 'white', borderColor: 'white' }}
                    onClick={() => navigate('/balance')}
                  >
                    View Balance
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: 140, 
                  bgcolor: 'rgba(25, 118, 210, 0.9)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    Total Tools
                  </Typography>
                  <Construction />
                </Box>
                <Typography variant="h4" sx={{ my: 'auto' }}>
                  {totalTools}
                </Typography>
                <Typography variant="body2" sx={{ mt: 'auto' }}>
                  {stakedTools} staked / {totalTools - stakedTools} unstaked
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: 140, 
                  bgcolor: 'rgba(46, 125, 50, 0.9)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    Deer NFTs
                  </Typography>
                  <Grass />
                </Box>
                <Typography variant="h4" sx={{ my: 'auto' }}>
                  {totalDeer}
                </Typography>
                <Typography variant="body2" sx={{ mt: 'auto' }}>
                  Staked
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: 140, 
                  bgcolor: 'rgba(237, 108, 2, 0.9)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    Worlds
                  </Typography>
                  <Landscape />
                </Box>
                <Typography variant="h4" sx={{ my: 'auto' }}>
                  {openWorlds} / {totalWorlds}
                </Typography>
                <Typography variant="body2" sx={{ mt: 'auto' }}>
                  Open / Total Worlds
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper 
                sx={{ 
                  p: 2, 
                  display: 'flex', 
                  flexDirection: 'column', 
                  height: 140, 
                  bgcolor: 'rgba(211, 47, 47, 0.9)',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">
                    Tournaments
                  </Typography>
                  <EmojiEvents />
                </Box>
                <Typography variant="h4" sx={{ my: 'auto' }}>
                  {upcomingTournaments.length}
                </Typography>
                <Typography variant="body2" sx={{ mt: 'auto' }}>
                  Upcoming tournaments
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Main Content Area */}
          <Grid container spacing={3} sx={{ background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 100%)', borderRadius: 2 ,color: 'white'}}>
            {/* Staking Pools */}
            {/* <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Staking Pools
                </Typography>
                
                {stakingPools.length > 0 ? (
                  <Grid container spacing={2}>
                    {stakingPools.map((pool: any) => (
                      <Grid item xs={12} sm={6} key={pool.id}>
                        <Card sx={{ bgcolor: 'rgba(0, 0, 0, 0.5)', height: '100%' }}>
                          <CardContent sx={{ color: 'white' }}>
                            <Typography variant="h6" color="primary">
                              Pool #{pool.id}
                            </Typography>
                            <Typography variant="body2" color="white" gutterBottom>
                              Min Stake: {pool.min_stake}
                            </Typography>
                            <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }}/>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between'}}>
                              <Typography variant="body2">Base APR:</Typography>
                              <Typography variant="body2">{parseFloat(pool.base_apr)}%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Monthly Increase:</Typography>
                              <Typography variant="body2">{parseFloat(pool.monthly_increase)}%</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Total Staked:</Typography>
                              <Typography variant="body2">{parseFloat(pool.total_staked)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2">Reward Type:</Typography>
                              <Chip 
                                label={pool.reward_type} 
                                size="small" 
                                color={pool.reward_type === 'ANTLER' ? 'primary' : 'secondary'} 
                              />
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body1">
                    No staking pools available at the moment.
                  </Typography>
                )}
              </Paper>
            </Grid> */}
            
            {/* Activity Feed */}
            {/* <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(75, 75, 9, 0.98)', borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Recent Activity
                </Typography>
                <List>
                  {recentTransactions.map((transaction, index) => (
                    <ListItem 
                      key={index}
                      divider={index !== recentTransactions.length - 1}
                      sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: transaction.type === 'stake' ? 'primary.main' : 
                            transaction.type === 'claim' ? 'success.main' : 'warning.main' }}>
                          {transaction.type === 'stake' ? <Diamond /> : 
                           transaction.type === 'claim' ? <TrendingUp /> : <Landscape />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText 
                        primary={`${transaction.type.charAt(0).toUpperCase()}${transaction.type.slice(1)}: ${transaction.amount}`} 
                        secondary={transaction.time}
                      />
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Upcoming Tournaments
                </Typography>
                {upcomingTournaments.length > 0 ? (
                  <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {upcomingTournaments.map((tournament: any, index: number) => (
                      <ListItem 
                        key={tournament.id}
                        divider={index !== upcomingTournaments.length - 1}
                        sx={{ borderColor: 'rgba(255,255,255,0.1)' }}
                      >
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: 'warning.main' }}>
                            <EmojiEvents />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                          primary={tournament.name || `Tournament #${tournament.id}`}
                          secondary={`Prize: ${tournament.prize || '100 ANTLER'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2">
                    No upcoming tournaments.
                  </Typography>
                )}
              </Paper>
            </Grid> */}
            
            {/* Quick Actions */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ 
                        py: 2, 
                        bgcolor: 'rgba(25, 118, 210, 0.8)', 
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 1)' } 
                      }}
                      onClick={() => navigate('/tools')}
                    >
                      Stake Tools
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ 
                        py: 2, 
                        bgcolor: 'rgba(46, 125, 50, 0.8)', 
                        '&:hover': { bgcolor: 'rgba(46, 125, 50, 1)' } 
                      }}
                      onClick={() => navigate('/tools')}
                    >
                      Claim Rewards
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ 
                        py: 2, 
                        bgcolor: 'rgba(237, 108, 2, 0.8)', 
                        '&:hover': { bgcolor: 'rgba(237, 108, 2, 1)' } 
                      }}
                      onClick={() => navigate('/')}
                    >
                      Explore Worlds
                    </Button>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      sx={{ 
                        py: 2, 
                        bgcolor: 'rgba(211, 47, 47, 0.8)', 
                        '&:hover': { bgcolor: 'rgba(211, 47, 47, 1)' } 
                      }}
                      onClick={() => navigate('/tokens')}
                    >
                      Exchange Tokens
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </>
      ) : (
        // Not logged in state
        <Paper sx={{ p: 3, bgcolor: 'rgba(0, 0, 0, 0.6)', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Welcome to WaxDeer
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please log in to view your dashboard and manage your assets.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={() => navigate('/login')}
          >
            Login Now
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default Dashboard;